import type { ReactNode } from 'react'

import { cn } from '../cn'

/**
 * The small bordered button beside a table row — `FRONTEND.md` §9.74.
 *
 * §9.3 found the primary button written fourteen ways. One level down, this one was defined
 * **seven times in four shapes**: four byte-identical copies, one that adds an `emerald` tone, one
 * that adds `amber`, and one that adds `disabled` and `hint`.
 *
 * Unlike §9.3's, those seven **already agreed** — same base, same neutral fallback, same
 * `text-{tone}-500 hover:bg-{tone}-500/10`. So this is not a normalisation that moves pixels; it is
 * one button that was copied, and every call site must render exactly what it rendered before. Each
 * difference between the shapes is an optional prop whose default is what the other six already do.
 */
export interface IconButtonProps {
  /** Names the action. Used for both `aria-label` and `title` unless `hint` says otherwise. */
  label: string
  /**
   * Why the action is unavailable, or what it will do — appended to the label for screen readers
   * and shown alone as the tooltip. Absent gives `aria-label={label} title={label}`, which is what
   * six of the seven call sites do.
   */
  hint?: string
  /** Absent is the neutral action, which is every default case in the seven. */
  tone?: 'red' | 'emerald' | 'amber'
  disabled?: boolean
  onClick: () => void
  children: ReactNode
  /**
   * Markers the button carries, spread as attributes - `SAAS.md §3.118`.
   *
   * The Tower's six hand-rolled copies each carried one keyed by the row they act on -
   * `data-package-edit={slug}`, `data-media-copy={url}` - which `tower.spec.ts` selects by. A `Record`
   * rather than a name-and-value pair, because that is the shape `DataTable.rowAttributes` already
   * established here and because a row action can want more than one.
   */
  attributes?: Record<string, string | number | boolean>
}

/** Kept beside the tone rather than interpolated: Tailwind cannot see a class it has to build. */
const TONES = {
  red: 'text-red-500 hover:bg-red-500/10',
  emerald: 'text-emerald-500 hover:bg-emerald-500/10',
  amber: 'text-amber-500 hover:bg-amber-500/10',
} as const

const NEUTRAL = 'text-muted-foreground hover:text-foreground hover:bg-muted'

/**
 * The class string this button renders — separated so it can be asserted without a DOM.
 *
 * §9.74 must not move a pixel, and the check for that is each of the four shapes producing what its
 * own local copy produced. `packages/ui` tests pure functions (`contrast.test.ts`); adding a jsdom
 * environment to assert one `className` would be a toolchain decision taken for a test's
 * convenience.
 */
export function iconButtonClass(tone?: IconButtonProps['tone']): string {
  return cn(
    // `disabled:opacity-40` on every instance, including the six that never disable one: a Tailwind
    // `disabled:` class matches only the `:disabled` pseudo-class, so on a button rendered without
    // the attribute it cannot move a pixel.
    'p-1.5 rounded-lg border border-border transition-colors disabled:opacity-40',
    tone === undefined ? NEUTRAL : TONES[tone],
  )
}

export function IconButton({
  label,
  hint,
  tone,
  disabled,
  onClick,
  children,
  attributes,
}: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...attributes}
      aria-label={hint === undefined ? label : `${label} - ${hint}`}
      title={hint ?? label}
      className={iconButtonClass(tone)}
    >
      {children}
    </button>
  )
}
